import AreaSelection from "Components/AreaSelection/AreaSelection";
import ConfirmToLeave from "Components/Common/ConfirmToLeave/ConfirmToLeave";
import ContentView from "Components/ContentView/ContentView";
import DeleteModal from "Components/DeleteModal/DeleteModal";
import Loader from "Components/Loading/Loader/Loader";
import { Area } from "Models/Areas/Area";
import { ChecklistItemTemplate } from "Models/Checklists/ChecklistItemTemplate";
import { ChecklistTemplate } from "Models/Checklists/ChecklistTemplate";
import { DataState } from "Models/Common/DataState";
import { RoleType } from "Models/Users/RoleType";
import "rc-dialog/assets/index.css";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IChecklistTemplateAction } from "Store/ChecklistTemplates/ChecklistTemplateAction";
import { IChecklistTemplateState } from "Store/ChecklistTemplates/ChecklistTemplateState";
import { IApplicationState } from "Store/Store";
import i18n from "Translations/i18n";
import { ChecklistItems } from "./ChecklistItems/ChecklistItems";
import ChecklistTemplateDetailHeader from "./ChecklistTemplateDetailHeader/ChecklistTemplateDetailHeader";
import styles from "./ChecklistTemplateDetailView.scss";
import { ChecklistTemplateInfo } from "./ChecklistTemplateInfo/ChecklistTemplateInfo";
import ChecklistTemplateSettings from "./ChecklistTemplateSettings/ChecklistTemplateSettings";

const checklistTemplateAction = ServiceProvider.get<IChecklistTemplateAction>(Types.IChecklistTemplateAction);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getChecklistTemplateByIdAsync: (checklistTemplateId: number) =>
        dispatch(checklistTemplateAction.getChecklistTemplateByIdAsync(checklistTemplateId)),
    saveChecklistTemplateAsync: (checklistTemplate: ChecklistTemplate, pictureFile?: File, addAnother?: boolean) =>
        dispatch(checklistTemplateAction.saveChecklistTemplateAsync(checklistTemplate, pictureFile, addAnother)),
    duplicateChecklistTemplateAsync: (checklistTemplate: ChecklistTemplate) =>
        dispatch(checklistTemplateAction.duplicateChecklistTemplateAsync(checklistTemplate)),
    deleteChecklistTemplateByIdAsync: (checklistTemplateId: number) =>
        dispatch(checklistTemplateAction.deleteChecklistTemplateByIdAsync(checklistTemplateId)),
    clearValidationErrorsByIdAsync: (checklistTemplateId?: number) =>
        dispatch(checklistTemplateAction.clearValidationErrorsByIdAsync(checklistTemplateId))
});

const mapStateToProps = (state: IApplicationState) => ({
    checklistTemplateState: state.checklistTemplateState
});

interface IRouteParams {
    checklistTemplateId: string; // must be type string since route params
}

interface IProps extends RouteComponentProps<IRouteParams> {
    checklistTemplateState: IChecklistTemplateState;
    saveChecklistTemplateAsync: (checklistTemplate: ChecklistTemplate, pictureFile?: File, addAnother?: boolean) => Action;
    getChecklistTemplateByIdAsync: (checklistTemplateId: number) => Action;
    duplicateChecklistTemplateAsync: (checklistTemplate: ChecklistTemplate) => Action;
    clearValidationErrorsByIdAsync: (checklistTemplateId?: number) => Action;
    deleteChecklistTemplateByIdAsync: (checklistTemplateId: number) => Action;
}

interface IState {
    checklistTemplate?: ChecklistTemplate;
    isLoading: boolean;
    isOpenDeleteModal: boolean;
    pictureFile?: File;
    validationErrors: string[];
    confirmToLeave: boolean;
    isDuplicating: boolean;
}

export class ChecklistTemplateDetailView extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.loadChecklistTemplate();
        this.checklistTemplateUpdated = this.checklistTemplateUpdated.bind(this);
        this.onInfoUpdated = this.onInfoUpdated.bind(this);
        this.onRoleChanged = this.onRoleChanged.bind(this);
        this.onSignatureRequiredChanged = this.onSignatureRequiredChanged.bind(this);
        this.onAreaSelectionChanged = this.onAreaSelectionChanged.bind(this);
        this.loadChecklistTemplate = this.loadChecklistTemplate.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.delete = this.delete.bind(this);
        this.performDelete = this.performDelete.bind(this);
        this.duplicate = this.duplicate.bind(this);
        this.save = this.save.bind(this);
        this.performSave = this.performSave.bind(this);
        this.saveAndAddAnother = this.saveAndAddAnother.bind(this);
        this.performSaveAndAddAnother = this.performSaveAndAddAnother.bind(this);
        this.onItemsUpdated = this.onItemsUpdated.bind(this);
        this.onDeleteChecklistClicked = this.onDeleteChecklistClicked.bind(this);
    }

    private loadChecklistTemplate() {
        const checklistIdParam = this.props.match.params.checklistTemplateId;
        if (checklistIdParam && checklistIdParam !== "new") {
            const checklistTemplateId = parseInt(checklistIdParam, 0);
            this.props.getChecklistTemplateByIdAsync(checklistTemplateId);
            this.state = {
                isLoading: true,
                isOpenDeleteModal: false,
                validationErrors: [],
                confirmToLeave: false,
                isDuplicating: false
            };
            this.props.clearValidationErrorsByIdAsync(checklistTemplateId);
        }
        else {
            this.state = {
                isLoading: false,
                isOpenDeleteModal: false,
                checklistTemplate: new ChecklistTemplate(),
                validationErrors: [],
                confirmToLeave: false,
                isDuplicating: false
            };
            this.props.clearValidationErrorsByIdAsync();
        }
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        let checklistTemplateId: number = -1;
        const partialState: Partial<IState> = {};

        if (nextProps.match.params.checklistTemplateId && nextProps.match.params.checklistTemplateId !== "new") {
            checklistTemplateId = parseInt(nextProps.match.params.checklistTemplateId, 0);

            if (
                nextProps.checklistTemplateState.detailChecklistTemplates &&
                nextProps.checklistTemplateState.detailChecklistTemplates[checklistTemplateId] &&
                (
                    !prevState.checklistTemplate ||
                    !prevState.checklistTemplate.id ||
                    nextProps.checklistTemplateState.detailChecklistTemplates[checklistTemplateId].lastUpdatedAt !== prevState.checklistTemplate.lastUpdatedAt
                )
            ) {
                partialState.checklistTemplate = nextProps.checklistTemplateState.detailChecklistTemplates[checklistTemplateId];
                partialState.confirmToLeave = false;

                if (prevState.isDuplicating) {
                    partialState.isDuplicating = false;
                }
            }
            if (nextProps.checklistTemplateState.detailChecklistTemplateStates) {
                const templateState = nextProps.checklistTemplateState.detailChecklistTemplateStates[checklistTemplateId];
                if (templateState === DataState.Deleted) {
                    nextProps.history.push("/checklisttemplates");
                }
                else if ((templateState === DataState.Loading) !== prevState.isLoading) {
                    partialState.isLoading = templateState === DataState.Loading;
                }
            }
        }

        if (nextProps.checklistTemplateState.detailChecklistTemplateValidationErrors
            && nextProps.checklistTemplateState.detailChecklistTemplateValidationErrors[checklistTemplateId]
            && nextProps.checklistTemplateState.detailChecklistTemplateValidationErrors[checklistTemplateId] !== prevState.validationErrors) {
            partialState.validationErrors = nextProps.checklistTemplateState.detailChecklistTemplateValidationErrors[checklistTemplateId];
        }

        return partialState;
    }

    public checklistTemplateUpdated() {
        this.setState({ confirmToLeave: true });
    }

    public onInfoUpdated(name: string, isMediaChanged: boolean, pictureFile?: File) {
        let template = this.state.checklistTemplate;
        if (isMediaChanged) {
            template = {
                ...template!,
                pictureUrl: pictureFile ? URL.createObjectURL(pictureFile) : undefined
            };
        }

        this.setState({
            checklistTemplate: {
                ...template!,
                name
            },
            pictureFile
        });
        this.checklistTemplateUpdated();
    }

    public onRoleChanged(newRole: RoleType) {
        this.setState({
            checklistTemplate: {
                ...this.state.checklistTemplate!,
                role: newRole
            }
        });
        this.checklistTemplateUpdated();
    }
    
    public onSignatureRequiredChanged(signatureRequired: boolean, doubleSignatureRequired: boolean) {
        this.setState({
            checklistTemplate: {
                ...this.state.checklistTemplate!,
                signatureRequired,
                doubleSignatureRequired
            }
        });
        this.checklistTemplateUpdated();
    }

    public onAreaSelectionChanged(selectedArea?: Area) {
        this.setState({
            checklistTemplate: {
                ...this.state.checklistTemplate!,
                area: selectedArea
            }
        });
        this.checklistTemplateUpdated();
    }

    public onDeleteChecklistClicked() {
        this.setState({
            isOpenDeleteModal: true
        });
    }

    public closeDeleteDialog() {
        this.setState({
            isOpenDeleteModal: false
        });
    }

    public delete() {
        if (this.state.checklistTemplate && this.state.checklistTemplate.id) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performDelete);
        }
    }

    public performDelete() {
        if (this.state.checklistTemplate && this.state.checklistTemplate.id) {
            this.props.deleteChecklistTemplateByIdAsync(this.state.checklistTemplate.id);
        }
    }

    public duplicate() {
        if (this.state.checklistTemplate) {
            this.setState({ isDuplicating: true });
            this.props.duplicateChecklistTemplateAsync(this.state.checklistTemplate);
        }
    }

    public save() {
        if (this.state.checklistTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSave);
        }
    }

    private performSave() {
        if (this.state.checklistTemplate) {
            this.props.saveChecklistTemplateAsync(this.state.checklistTemplate, this.state.pictureFile);
        }
    }

    public saveAndAddAnother() {
        if (this.state.checklistTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSaveAndAddAnother);
        }
    }

    private performSaveAndAddAnother() {
        if (this.state.checklistTemplate) {
            this.props.saveChecklistTemplateAsync(this.state.checklistTemplate, this.state.pictureFile, true);
            this.setState({
                checklistTemplate: { ...this.state.checklistTemplate, id: undefined }
            });
        }
    }

    public onItemsUpdated(items: ChecklistItemTemplate[]) {
        this.setState({
            checklistTemplate: {
                ...this.state.checklistTemplate!,
                items,
            },
        });
        this.checklistTemplateUpdated();
    }

    public render() {
        return (
            <>
                {this.state.checklistTemplate ? (
                    <>
                        <ConfirmToLeave confirmToLeave={this.state.confirmToLeave} />
                        <ChecklistTemplateDetailHeader canDuplicate={!this.state.confirmToLeave} isDuplicating={this.state.isDuplicating} onDeleteChecklistClicked={this.onDeleteChecklistClicked} duplicate={this.duplicate} save={this.save} saveAndAddAnother={this.saveAndAddAnother} checklistTemplate={this.state.checklistTemplate} />
                        <ContentView className={`${styles.validationErrors} ${this.state.validationErrors && this.state.validationErrors.length ? styles.visible : styles.hidden}`}>
                            <div className={styles.error}>
                                <h2>{i18n.t("checklists.validationErrors.foundErrors")}</h2>
                                <ul>
                                    {
                                        this.state.validationErrors.map((validationError, index) => {
                                            return (<li key={validationError + "-" + index}>{i18n.t(`checklists.validationErrors.${validationError}`)}</li>);
                                        })
                                    }
                                </ul>
                            </div>
                        </ContentView>
                        <ChecklistTemplateInfo checklistTemplate={this.state.checklistTemplate} onChange={this.onInfoUpdated} />
                        <ChecklistItems checklistTemplate={this.state.checklistTemplate} onChange={this.onItemsUpdated} />
                        <AreaSelection className={styles.areaSearch} backgroundIsGray={true} selectedArea={this.state.checklistTemplate.area} onSelectionChanged={this.onAreaSelectionChanged} showAreaNavigator={true} title={i18n.t("tasks.area.title")} />
                        <ChecklistTemplateSettings selectedRole={this.state.checklistTemplate.role} selectedSingleSignatureRequired={this.state.checklistTemplate.signatureRequired} selectedDoubleSignatureRequired={this.state.checklistTemplate.doubleSignatureRequired} onRoleChanged={this.onRoleChanged} onSignatureRequiredChanged={this.onSignatureRequiredChanged} />
                        <DeleteModal visible={this.state.isOpenDeleteModal} onDelete={this.delete} onCancel={this.closeDeleteDialog} message={i18n.t("checklists.confirmDeleteText")} />
                    </>
                ) : this.state.isLoading ? (
                    <Loader />
                ) : (
                            <>{i18n.t("checklists.checklistTemplateNotFound")}</>
                        )}
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChecklistTemplateDetailView);


// WEBPACK FOOTER //
// ./src/Components/Pages/ChecklistTemplateDetailView/ChecklistTemplateDetailView.tsx